import React, { FC, useMemo, useState } from 'react';
import { keyBy, omit } from 'lodash';
import styled from 'styled-components';

import ActionsFooter, { WarnProps } from 'src/presentation/components/pages/shared/ActionsFooter';
import AuthenticationEmailSection from './sections/AuthenticationEmailSection';
import { Body, PageHeader } from 'src/presentation/components/forms/Layout';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import CustomerIdentityAuthenticationSection from 'src/presentation/components/pages/threads/sections/CustomerIdentityAuthenticationSection';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import MainSection from 'src/presentation/components/pages/threads/sections/MainSection';
import newThreadTemplate from 'src/presentation/components/pages/threads/helpers/newThreadTemplate';
import OrderStatusSection from 'src/presentation/components/pages/threads/sections/OrderStatusSection';
import { STATUS_PERSISTING, useResourceValues } from 'src/presentation/hooks/useResource';
import threadsResource from 'src/actions/threads/threadsResource';
import useDirtyFormConfirmation from 'src/presentation/components/pages/shared/hooks/useDirtyFormConfirmation';
import useOnSave, { ThreadWithNodeMap } from 'src/presentation/components/pages/threads/hooks/useOnSave';
import ErrorSection from './sections/ErrorSection';
import { translate } from './helpers/translateValues';
import Thread from 'src/models/Thread/Thread';
import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import LinkOutStroke from '@ad-scripts/presentation/components/common/icons/stroke/link-out-stroke';
import { CHAT_BRAND } from 'src/constants/branding';

const ThreadEditorForm = ({ actionsRef, errors, item, root, isThreadDependencySatisfied }) => {
  const { add, update, validateThread } = threadsResource.effects;
  const { resetErrors } = threadsResource.actions;
  const { persistingState } = useResourceValues(threadsResource);

  const defaultItem: any = translate<ThreadWithNodeMap>(newThreadTemplate, { reverse: true });

  const transformedItem = useMemo(() => {
    const translatedItem = item ? translate<Thread>(item, { reverse: true }) : undefined;

    return translatedItem
      ? { ...omit(translatedItem, ['nodes']), nodesMap: keyBy(translatedItem.nodes, 'ivrNode.id') }
      : undefined;
  }, [item]);

  const [isComplete, setComplete] = useState(false);

  const handleSave = useOnSave({ setComplete });

  const formContext = useForm({
    add,
    defaultItem,
    errors,
    item: item ? transformedItem : undefined,
    onSave: handleSave,
    resetErrors,
    root,
    update,
    validate: validateThread,
  });

  const { isNew, onClose, onSave } = formContext;

  const { handleCancel, handleStay, isConfirmCancelModalVisible, isModified } = useDirtyFormConfirmation({
    formContext,
    isComplete,
    setComplete,
  });

  const handleNeedHelp = () => {
    window.open('https://help.gladly.com/docs/create-thread/', 'help');
  };

  let warnProps: WarnProps | undefined;
  if (!isThreadDependencySatisfied) {
    warnProps = {
      submitText: 'Got it',
      cancelText: 'Cancel',
      title: 'A reminder to set up your order management system',
      text: 'You must have Shopify, BigCommerce, Magento, or a custom Lookup Adaptor linked to an OMS for customers to view their orders.',
    };
  }

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <PageHeader
          headerText={isNew ? 'Create New Thread' : `Edit ${item.name}`}
          descriptionText={`Assign the messages and button labels that will be displayed in the ${CHAT_BRAND.name}.`}
        />
        {!isThreadDependencySatisfied ? <WarningContent /> : null}
        <StyledBody>
          <MainSection />
          <CustomerIdentityAuthenticationSection />
          <AuthenticationEmailSection />
          <OrderStatusSection />
          <ErrorSection />
        </StyledBody>

        <ActionsFooter
          onClose={handleCancel}
          onSave={onSave}
          ref={actionsRef}
          onHelp={handleNeedHelp}
          isLoading={persistingState === STATUS_PERSISTING}
          warnProps={warnProps}
        />
      </Form>
      <ConfirmCancelModal
        name="Thread"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
    </>
  );
};

const WarningContent: FC = () => {
  return (
    <WarningContentContainer>
      <AlertWarning>
        <div>
          <b>Note: </b>
          You must have Shopify, BigCommerce, Magento, or a custom Lookup Adaptor linked to an OMS for customers to view
          their orders. <StyledExternalLink to={'https://help.gladly.com'} label={'Learn why'} />
          <StyledLinkedOut />
        </div>
      </AlertWarning>
    </WarningContentContainer>
  );
};

const StyledLinkedOut = styled(LinkOutStroke)`
  width: 16px;
  height: 16px;
  vertical-align: text-bottom;
  padding-left: 2px;
`;

const WarningContentContainer = styled.div`
  width: 1113px;
  max-width: 1113px;
  padding: 0 80px;
  padding-bottom: 16px;
`;

const StyledExternalLink = styled(ExternalLink)`
  text-decoration: underline;
  color: ${p => p.theme.colors.black};

  &:hover {
    text-decoration: underline;
  }
`;

const StyledBody = styled(Body)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  max-width: 1230px;
  width: 1230px;
`;

export default ThreadEditorForm;
